import React, { useEffect, useState } from "react"
import { Button, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { FeedFilterValues, FeedType, TYPE_GROUPS } from "./types"

interface FeedFiltersProps {
  value: FeedFilterValues
  onChange: (next: FeedFilterValues) => void
}

const FeedFilters = ({ value, onChange }: FeedFiltersProps) => {
  const { t } = useTranslation("global")
  const [agents, setAgents] = useState<{ id: string; name: string }[]>([])
  const [search, setSearch] = useState(value.search)

  useEffect(() => setSearch(value.search), [value.search])

  useEffect(() => {
    const user: any = parsedUser()
    API.getAction("users/list", `OrganizationId=${user?.OrganizationId}`)
      .then(res => {
        if (res.data?.status !== "ok") return
        const list = (res.data.response || [])
          .map((u: any) => ({ id: u.userId || u.id, name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email }))
          .filter((u: any) => u.id)
          .sort((a: any, b: any) => a.name.localeCompare(b.name))
        setAgents(list)
      })
      .catch(() => setAgents([]))
  }, [])

  // Debounce search so typing doesn't reload the feed on every keystroke.
  useEffect(() => {
    if (search === value.search) return
    const timer = window.setTimeout(() => onChange({ ...value, search }), 400)
    return () => window.clearTimeout(timer)
  }, [search])

  const isGroupActive = (types: FeedType[]) => types.every(type => value.types.includes(type))

  const toggleGroup = (types: FeedType[]) => {
    const next = isGroupActive(types)
      ? value.types.filter(type => !types.includes(type))
      : [...value.types, ...types.filter(type => !value.types.includes(type))]
    onChange({ ...value, types: next })
  }

  return (
    <div className="feed-filters">
      <div className="feed-filters-title">{t("Filtros")}</div>

      <div className="feed-chips mb-3">
        <button
          type="button"
          className={`feed-chip ${value.types.length === 0 ? "active" : ""}`}
          onClick={() => onChange({ ...value, types: [] })}
        >
          {t("Todos")}
        </button>
        {TYPE_GROUPS.map(g => (
          <button
            key={g.key}
            type="button"
            className={`feed-chip ${isGroupActive(g.types) ? "active" : ""}`}
            onClick={() => toggleGroup(g.types)}
          >
            {t(g.label)}
          </button>
        ))}
      </div>

      <Form.Group className="mb-3">
        <Form.Label className="small text-muted">{t("Buscar")}</Form.Label>
        <Form.Control
          size="sm"
          type="search"
          value={search}
          placeholder={t("Lugar, código o conduce")}
          onChange={e => setSearch(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="small text-muted">{t("Agente")}</Form.Label>
        <Form.Select size="sm" value={value.agentId} onChange={e => onChange({ ...value, agentId: e.target.value })}>
          <option value="">{t("Todos los agentes")}</option>
          {agents.map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="small text-muted">{t("Ir a fecha")}</Form.Label>
        <Form.Control
          size="sm"
          type="date"
          max={moment().format("YYYY-MM-DD")}
          value={value.before ? moment(value.before).subtract(1, "day").format("YYYY-MM-DD") : ""}
          onChange={e =>
            onChange({
              ...value,
              // Show everything up to the end of the chosen day.
              before: e.target.value ? moment(e.target.value).add(1, "day").startOf("day").toISOString() : "",
            })
          }
        />
      </Form.Group>

      {(value.types.length > 0 || value.agentId || value.search || value.before) && (
        <Button
          size="sm"
          variant="outline-secondary"
          className="w-100"
          onClick={() => onChange({ types: [], agentId: "", search: "", before: "" })}
        >
          {t("Limpiar filtros")}
        </Button>
      )}
    </div>
  )
}

export default FeedFilters
